import {
  Button,
  Col,
  Form,
  FormGroup,
  FormSelect,
  Modal,
  Row,
} from "react-bootstrap";
import FileUploader from "../../../../@ui/FileUploader";
import { Formik } from "formik";
import { triggerImportSchema } from "./schema";
import { ErrorText } from "../../../../@ui/ErrorText";
import styled from "styled-components";
import ThemedModal from "../../../../@ui/Modal/ThemedModal";
import LoadingSpinner from "../../../Loading/LoadingSpinner";
import { DateTime } from "luxon";
import { useState } from "react";

interface TriggerImportModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (quarter: string, file?: File | null) => void;
  isSubmitting: boolean;
  hasErrored: boolean;
}

interface TriggerForm {
  rlFile?: File | null;
  quarter: string;
}

const getQuarter = (date: DateTime): string => {
  return `${date.year} Q${date.quarter}`;
};

const StyledModalTitle = styled(Modal.Title)`
  font-size: 1.25rem;
`;

const TriggerImportModal = ({
  show,
  onClose,
  onSubmit,
  isSubmitting,
  hasErrored,
}: TriggerImportModalProps) => {
  const now = DateTime.now();
  const currentQuarter = getQuarter(now);
  const initialValues: TriggerForm = {
    quarter: currentQuarter,
  };

  const quarterOptions = [
    getQuarter(now.minus({ months: 12 })),
    getQuarter(now.minus({ months: 9 })),
    getQuarter(now.minus({ months: 6 })),
    getQuarter(now.minus({ months: 3 })),
    currentQuarter,
    getQuarter(now.plus({ months: 3 })),
    getQuarter(now.plus({ months: 6 })),
    getQuarter(now.plus({ months: 9 })),
    getQuarter(now.plus({ months: 12 })),
  ];

  const submit = (values: TriggerForm) => {
    const { rlFile, quarter } = values;
    onSubmit(quarter, rlFile);
  };

  return (
    <ThemedModal centered show={show} onHide={onClose}>
      <Formik
        onSubmit={submit}
        initialValues={initialValues}
        validationSchema={triggerImportSchema}
      >
        {({
          handleSubmit,
          setFieldValue,
          setTouched,
          errors,
          handleBlur,
          handleChange,
          values,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Header className="bg-secondary text-white" closeButton>
              <StyledModalTitle>Trigger RouteLine Import</StyledModalTitle>
            </Modal.Header>
            <Modal.Body>
              <Row className="justify-content-center mb-2">
                <Col className="col-md-10">
                  <FormGroup className="w-100 mt-2">
                    <Form.Label>Upload Route Line Archive: </Form.Label>
                    <FileUploader
                      onClear={() => setFieldValue("rlFile", undefined)}
                      onChange={(file) => {
                        setFieldValue("rlFile", file);
                      }}
                      onBlur={(_) => setTouched({ rlFile: true })}
                    />
                  </FormGroup>
                  {errors.rlFile && (
                    <ErrorText className="text-danger">
                      RouteLine file required
                    </ErrorText>
                  )}
                </Col>
              </Row>
              <Row className="justify-content-center">
                <Col className="col-md-10">
                  <Form.Group>
                    <FormSelect
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.quarter}
                      name="quarter"
                    >
                      {quarterOptions.map((quarter, index) => (
                        <option key={index} value={quarter}>
                          {quarter}
                        </option>
                      ))}
                    </FormSelect>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              {!hasErrored && (
                <>
                  {isSubmitting && (
                    <span className="text-warning">
                      This might take a while, <i>don't refresh</i>
                    </span>
                  )}
                  <Button
                    disabled={isSubmitting}
                    onClick={() => {}}
                    type="submit"
                    variant="primary"
                  >
                    {!isSubmitting && <>Trigger Import</>}
                    {isSubmitting && (
                      <>
                        <LoadingSpinner textColor="text-light" />
                      </>
                    )}
                  </Button>
                </>
              )}
              {hasErrored && (
                <>
                  <span className="text-danger">
                    These has been an error attempting to start the import!
                  </span>
                </>
              )}
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </ThemedModal>
  );
};

export default TriggerImportModal;
